import * as queryString from "query-string";

import React, { Component } from "react";

import Headshots from "components/player/Headshots";
import { Helmet } from "react-helmet";
import Icon from "components/general/Icon";
import Playlist from "components/player/Playlist";
import PopoverMenu from "components/player/PopoverMenu";
import PropTypes from "prop-types";
import ReactPlayer from "react-player/file";
import { TimeSpent } from "utils/timeSpent";
import Tippy from "@tippyjs/react/headless";
import { analytics } from "utils/analytics";
import { dateToText } from "utils/helpers";
import { followCursor } from "tippy.js";
import getBlobDuration from "get-blob-duration";
import { hexToCSSFilter } from "hex-to-css-filter";
import iconPauseWhite from "images/ic_pause_white.png";
import iconPlayWhite from "images/ic_play_white.png";
import imageBadge from "images/img_badge.png";
import { publicAssetsDomain } from "environment";
import sortBy from "lodash/sortBy";
import styled from "styled-components";
import theme from "styles/theme";

const StyledReactPlayer = styled(ReactPlayer)`
  display: none;
`;

const SizingContainer = styled.div`
  container-type: inline-size;
`;

const Container = styled.div`
  width: 100%;
  min-width: 256px;
  box-sizing: border-box;

  color: ${(props) => props.color};
  background-color: ${(props) => props.$backgroundColor};
  border-radius: 16px;
  overflow: hidden;
`;

const PlayerContainer = styled.div`
  display: flex;
  column-gap: ${theme.spacing.unit * 1}px;
  align-items: center;
  padding: ${theme.spacing.unit * 1}px ${theme.spacing.unit * 2}px;
  background-color: transparent;
`;

const Info = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  row-gap: 4px;
`;

const InfoTitle = styled.div`
  font-size: 20px;
  font-family: Inter;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;

  ${theme.container.medium`
    font-size: 14px;
    font-family: Inter;
    font-style: normal;
    font-weight: 600;
    line-height: 100%;
  `};

  ${theme.container.extraSmall`
    font-size: 12px;
    font-family: Inter;
    font-style: normal;
    font-weight: 600;
    line-height: 100%;
  `};
`;

const InfoSubtitle = styled.div`
  font-size: 14px;
  font-family: Inter;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;

  ${theme.container.medium`
    font-size: 14px;
    font-family: Inter;
    font-style: normal;
    font-weight: 400;
    line-height: 100%;
  `};

  ${theme.container.extraSmall`
    font-size: 12px;
    font-family: Inter;
    font-style: normal;
    font-weight: 400;
    line-height: 100%;
  `};
`;

const RightContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: ${(props) =>
    props.$hidePuckBranding ? "center" : "space-between"};
  height: ${(props) => (props.$hidePuckBranding ? "auto" : "120px")};
  padding: ${theme.spacing.unit * 1}px 0;
  box-sizing: border-box;

  ${theme.container.medium`
    height: ${(props) => (props.$hidePuckBranding ? "auto" : "87px")};
  `};
`;

const Badge = styled.img`
  height: 13px;
  align-self: flex-end;

  ${(props) =>
    `filter: brightness(0) saturate(100%) ${
      hexToCSSFilter(props.color).filter
    };`};
`;

const PlayerControlsContainer = styled.div`
  display: flex;
  column-gap: ${theme.spacing.unit * 1}px;
  justify-content: space-between;
  align-items: center;
`;

const SingleTrackTimer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;

  width: 56px;
  height: 24px;

  font-family: Inter;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 100%;
`;

const PlayPauseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  background-color: ${(props) => props.$backgroundColor};

  cursor: pointer;
`;

const TrackPlaceholder = styled.div`
  height: 8px;
  width: 100%;
  background-color: ${(props) => props.$backgroundColor};
`;

const TrackContainer = styled.div`
  position: relative;

  background-color: ${(props) => props.$backgroundColor};
`;

const TrackHoverBelow = styled.div`
  position: absolute;
  top: 0;
  height: 100%;
  width: 100%;

  pointer-events: none;
  z-index: 1;

  background: ${(props) => `linear-gradient(
    to right,
    ${props.$color} 0%,
    ${props.$color} calc(${props.$rangeHoverPercentage} * 100%),
    transparent calc(${props.$rangeHoverPercentage} * 100%),
    transparent 100%
  )`};
`;

const TrackHoverAbove = styled.div`
  position: absolute;
  top: 0;
  height: 100%;
  width: 100%;

  pointer-events: none;
  z-index: 3;

  background: ${(props) => `linear-gradient(
    to right,
    ${props.$color} 0%,
    ${props.$color} calc(${props.$rangeHoverPercentage} * 100%),
    transparent calc(${props.$rangeHoverPercentage} * 100%),
    transparent 100%
  )`};
`;

const Track = styled.input`
  cursor: pointer;
  -webkit-appearance: none; /* Hides the slider so that custom slider can be made */
  width: 100%; /* Specific width is required for Firefox. */
  height: 8px;
  background: transparent; /* Otherwise white in Chrome */
  margin: 0;

  position: relative;
  z-index: 2;

  display: block;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    visibility: hidden;
    height: 8px;
    width: 8px;
    appearance: none;
  }

  /* All the same stuff for Firefox */
  &::-moz-range-thumb {
    visibility: hidden;
    height: 8px;
    width: 8px;
    appearance: none;
  }

  &::-webkit-slider-runnable-track {
    cursor: pointer;
    width: 100%;
    height: 8px;
    box-sizing: border-box;
    background: ${(props) => `linear-gradient(
      to right,
      ${props.color} 0%,
      ${props.color} calc(${props.played} * 100%),
      transparent calc(${props.played} * 100%),
      transparent 100%
    )`};
  }

  &::-moz-range-track {
    cursor: pointer;
    width: 100%;
    height: 8px;
    box-sizing: border-box;
    background: ${(props) => `linear-gradient(
      to right,
      ${props.color} 0%,
      ${props.color} calc(${props.played} * 100%),
      transparent calc(${props.played} * 100%),
      transparent 100%
    )`};
  }

  &::-moz-range-progress {
    cursor: pointer;
    height: 8px;
    box-sizing: border-box;
    background-color: ${(props) => props.color};
  }
`;

const Tooltip = styled.div`
  background: rgba(44, 44, 44, 0.75);
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.3),
    0px 1px 3px 1px rgba(0, 0, 0, 0.15);
  border-radius: 4px;

  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.unit * 1}px;

  font-family: "Inter";
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: ${theme.colors.white};
`;

export class Player extends Component {
  dummyAudioSrc = `https://${publicAssetsDomain}/quarter-second-of-silence.mp3`;

  constructor(props) {
    super(props);

    this.state = {
      isPlaying: false,
      played: 0,
      playedSeconds: 0,
      hasTrackChanged: false,

      playingJobClipTrack: null,
      shouldResetPlayingJobClipTrack: false,

      seekTrackStartSec: 0,

      rangeHoverPercentage: null,

      isExpanded: false,
      // Only start animating height after it has been expanded once
      isHeightAnimationEnabled: false,
    };
  }

  componentDidMount() {
    const { jobClip } = this.props;

    this.checkIsHoverSupported();

    // If we have a job clip with one track, load it
    if (jobClip?.tracks?.length === 1) {
      this.setState({
        playingJobClipTrack: jobClip.tracks[0],
      });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { playingJobClipTrack, shouldResetPlayingJobClipTrack, isPlaying } =
      this.state;
    const {
      jobClip,
      // onPlayingJobClipReset
      onIsPlayingChange,
    } = this.props;

    if (isPlaying !== prevState.isPlaying && onIsPlayingChange) {
      onIsPlayingChange(isPlaying);
    }

    // If job clip has changed, wait for player ready
    if (
      jobClip?.permalink &&
      jobClip.permalink !== prevProps.jobClip?.permalink
    ) {
      const newState = {
        hasTrackChanged: true,
      };
      if (jobClip?.tracks?.length === 1) {
        newState.playingJobClipTrack = jobClip.tracks[0];
      }
      this.setState({
        ...newState,
      });
    } else if (
      playingJobClipTrack?.permalink &&
      playingJobClipTrack.permalink !== prevState.playingJobClipTrack?.permalink
    ) {
      this.resetJobClipTrack();
    } else if (
      shouldResetPlayingJobClipTrack &&
      !prevProps.shouldResetPlayingJobClipTrack
    ) {
      this.resetJobClipTrack();
      this.handlePlayingJobClipTrackReset();
    }
  }

  componentWillUnmount() {
    const { onIsPlayingChange } = this.props;

    // Pause the player
    this.handlePlayerPause();

    if (onIsPlayingChange) {
      onIsPlayingChange(false);
    }
  }

  handleShowMoreClick = () => {
    this.setState({
      isExpanded: true,
      isHeightAnimationEnabled: true,
    });
  };

  handleShowLessClick = () => {
    this.setState({
      isExpanded: false,
    });
  };

  handlePlayerError = (error) => {
    const { jobClip } = this.props;
    const queryParams = queryString.parse(document.location.search);

    console.log("ReactPlayer onError", error);
    analytics.track(`${this.props.analyticsSrc} Player Error`, {
      type: "audio",
      error,
      jobClip,
      queryParams,
    });
  };

  checkIsHoverSupported = () => {
    const isHoverSupportedQuery = theme.media.hoverSupportedQuery;

    this.setState({
      isHoverSupported: isHoverSupportedQuery.matches,
    });
  };

  // reactPlayerState: { played: number, playedSeconds: number, loaded: number, loadedSeconds: number }
  handleProgress = (reactPlayerState) => {
    const { playingJobClipTrack, seeking } = this.state;
    const { onProgress } = this.props;

    if (!playingJobClipTrack) {
      return;
    }

    if (onProgress) {
      onProgress(playingJobClipTrack, reactPlayerState);
    }

    const jobClipTrackStartSecs =
      playingJobClipTrack.recording.audios[0].secRangeStart || 0;
    const jobClipTrackEndSecs =
      playingJobClipTrack.recording.audios[0].secRangeEnd ||
      playingJobClipTrack.recording.audios[0].duration;

    // Calculate played ourselves
    const playedPercent =
      (reactPlayerState.playedSeconds - jobClipTrackStartSecs) /
      (jobClipTrackEndSecs - jobClipTrackStartSecs);

    // console.log("jobClipEndSecs", jobClipTrackEndSecs);
    // console.log("jobClipStartSecs", jobClipTrackStartSecs);
    // console.log("playedSeconds", reactPlayerState.playedSeconds);

    const remainingSeconds =
      jobClipTrackEndSecs - reactPlayerState.playedSeconds;

    // console.log(remainingSeconds);

    if (!seeking) {
      this.setState({
        played: playedPercent,
        playedSeconds: reactPlayerState.playedSeconds - jobClipTrackStartSecs,
        remainingSeconds,
      });
    }

    if (reactPlayerState.playedSeconds >= jobClipTrackEndSecs) {
      this.handlePlayerFinish();
    }
  };

  handleRangeMouseMove = (e) => {
    // console.log(e);

    let nativeEvent = e;
    if (e.nativeEvent) {
      nativeEvent = e.nativeEvent;
    }

    let rangeHoverPercentage = null;
    rangeHoverPercentage = nativeEvent.offsetX / nativeEvent.target.clientWidth;
    // console.log(rangeHoverPercentage);

    this.setState({
      rangeHoverPercentage,
    });
  };

  progressBar = (played) => {
    const { playingJobClipTrack, rangeHoverPercentage } = this.state;
    const { jobClip } = this.props;

    const brandingData = jobClip?.employerProfile?.brandingData || {};

    if (!playingJobClipTrack) {
      return (
        <TrackPlaceholder $backgroundColor={brandingData.secondaryStep1Color} />
      );
    }

    const jobClipTrackStartSecs =
      playingJobClipTrack.recording.audios[0].secRangeStart || 0;
    const jobClipTrackEndSecs =
      playingJobClipTrack.recording.audios[0].secRangeEnd ||
      playingJobClipTrack.recording.audios[0].duration;

    // Progress string
    // const playedPercent = played;
    // const progressSecs =
    //   Math.max(0, playedPercent) *
    //   (jobClipTrackEndSecs - jobClipTrackStartSecs);
    // const progressString = dateToText(new Date(progressSecs * 1000));

    // Duration string
    // const durationString = dateToText(
    //   new Date((jobClipTrackEndSecs - jobClipTrackStartSecs) * 1000)
    // );

    let tooltipString = null;
    if (rangeHoverPercentage) {
      const hoveredSecs =
        Math.max(0, rangeHoverPercentage) *
        (jobClipTrackEndSecs - jobClipTrackStartSecs);
      // console.log(hoveredSecs);
      tooltipString = dateToText(hoveredSecs);
    }

    return (
      <TrackContainer
        $rangeHoverPercentage={rangeHoverPercentage}
        $backgroundColor={brandingData.secondaryStep1Color}
      >
        {rangeHoverPercentage && rangeHoverPercentage > played && (
          <TrackHoverBelow
            $played={played}
            $rangeHoverPercentage={rangeHoverPercentage}
            $color={brandingData.secondaryStep2Color}
          />
        )}
        <Tippy
          // followCursor={"horizontal"}
          // plugins={[followCursor]}
          // role={"tooltip"}
          // // trigger="mouseenter focus"
          // trigger="mouseenter"
          // animation={false}
          // interactive={false}

          // onCreate={this.handleCreate}
          placement={"top"}
          offset={[0, 4]}
          // interactive={true}
          // appendTo={document.body}
          // trigger={"mouseenter focusin click"}
          trigger={"mouseenter"}
          followCursor={"horizontal"}
          plugins={[followCursor]}
          render={(attrs) =>
            tooltipString ? <Tooltip>{tooltipString}</Tooltip> : null
          }
          hideOnClick={false}
        >
          <div>
            <Track
              type="range"
              min={0}
              max={1}
              step="any"
              // defaultValue={played}
              value={played}
              played={played}
              onMouseDown={this.handleSeekStart}
              onTouchStart={this.handleSeekStart}
              onChange={this.handleSeekChange}
              onMouseUp={this.handleSeekEnd}
              onTouchEnd={this.handleSeekEnd}
              color={brandingData.buttonColor}
              onMouseMove={this.handleRangeMouseMove}
              onMouseLeave={() => {
                this.setState({ rangeHoverPercentage: null });
              }}
            />
          </div>
        </Tippy>
        {rangeHoverPercentage && rangeHoverPercentage <= played && (
          <TrackHoverAbove
            $played={played}
            $rangeHoverPercentage={rangeHoverPercentage}
            $color={brandingData.buttonHoverColor}
          />
        )}
      </TrackContainer>
    );
  };

  handleSeekStart = (e) => {
    this.setState({
      seeking: true,
    });
  };

  handleSeekChange = (e) => {
    this.setState({ played: parseFloat(e.target.value) });
  };

  handleSeekEnd = (e) => {
    const { playingJobClipTrack } = this.state;

    const jobClipTrackStartSecs =
      playingJobClipTrack.recording.audios[0].secRangeStart || 0;
    const jobClipTrackEndSecs =
      playingJobClipTrack.recording.audios[0].secRangeEnd ||
      playingJobClipTrack.recording.audios[0].duration;

    const playedPercent = parseFloat(e.target.value);
    const seekedSecs =
      playedPercent * (jobClipTrackEndSecs - jobClipTrackStartSecs) +
      jobClipTrackStartSecs;

    this.player.seekTo(seekedSecs, "seconds");
    this.setState({ seekTrackStartSec: seekedSecs });
  };

  handleSeek = (e) => {
    this.setState({ seeking: false });
  };

  confirmDuration = async () => {
    const { src } = this.props;

    const goodDuration = await getBlobDuration(src);
    this.setState({ duration: goodDuration });
  };

  handleDuration = (duration) => {
    if (duration !== Infinity) {
      this.setState({ duration: duration });
    } else {
      this.confirmDuration();
    }
  };

  resetJobClipTrack = () => {
    const { playingJobClipTrack } = this.state;

    // Figure out where to seek to
    const jobClipTrackStartSecs =
      playingJobClipTrack.recording.audios[0].secRangeStart;
    if (jobClipTrackStartSecs) {
      this.player.seekTo(jobClipTrackStartSecs);
    } else {
      this.player.seekTo(0);
    }
  };

  handlePlayerReady = () => {
    const { hasTrackChanged } = this.state;

    if (hasTrackChanged) {
      this.setState(
        {
          hasTrackChanged: false,
        },
        () => this.resetJobClipTrack()
      );
    }
  };

  handlePlayerFinish = () => {
    const { playingJobClipTrack } = this.state;

    const jobClipTrackDuration =
      this.durationFromJobClipTrack(playingJobClipTrack);

    analytics.track(`${this.props.analyticsSrc} Player Played Track`, {
      ...this.jobClipAnalyticsPayload(),
      durationPlayed: jobClipTrackDuration,
      percentPlayed: 100,
    });

    this.goNextTrack();
  };

  durationFromJobClipTrack = (jobClipTrack) => {
    const jobClipTrackStartSecs =
      jobClipTrack.recording.audios[0].secRangeStart || 0;
    const jobClipTrackEndSecs =
      jobClipTrack.recording.audios[0].secRangeEnd ||
      jobClipTrack.recording.audios[0].duration;
    const jobClipTrackDuration = jobClipTrackEndSecs - jobClipTrackStartSecs;

    return jobClipTrackDuration;
  };

  ref = (player) => {
    this.player = player;
  };

  reactPlayer = (src) => {
    const { isPlaying } = this.state;
    // const { isPlaying, onPlay, onPause } = this.props;

    return (
      <StyledReactPlayer
        ref={this.ref}
        height={0}
        width={0}
        playing={isPlaying}
        controls={false}
        url={src}
        config={{
          file: {
            forceAudio: true,
          },
        }}
        onSeek={this.handleSeek}
        onEnded={this.handlePlayerFinish}
        onError={this.handlePlayerError}
        onProgress={this.handleProgress}
        onDuration={this.handleDuration}
        // onStart={this.handlePlayerStart}
        progressInterval={50}
        // muted={isMuted}
        onReady={this.handlePlayerReady}
        onPlay={this.handlePlayerPlay}
        onPause={this.handlePlayerPause}
      />
    );
  };

  handleJobClipTrackClick =
    (jobClipTrack, isJobClipPlay = false) =>
    () => {
      const { isPlaying, playingJobClipTrack } = this.state;

      this.stopJobClipTrackTimer("handleJobClipTrackClick");

      // Check if we're changing tracks
      if (
        playingJobClipTrack &&
        playingJobClipTrack.permalink === jobClipTrack.permalink
      ) {
        if (isPlaying) {
          this.handlePlayerPause(true);
        } else {
          this.handlePlayerPlay(true);
        }
      } else {
        this.setState(
          {
            playingJobClipTrack: jobClipTrack,
          },
          () => {
            this.handlePlayerPlay(true, isJobClipPlay);
          }
        );
      }
    };

  handlePlayPauseOrStartClick = () => {
    const { playingJobClipTrack } = this.state;
    const { jobClip } = this.props;

    if (playingJobClipTrack) {
      this.handlePlayPauseClick();
    } else {
      // console.log("faking a track click");
      this.handleJobClipTrackClick(jobClip.jobClipTracks[0], true)();
    }
  };

  handlePlayPauseClick = () => {
    const { isPlaying } = this.state;

    if (isPlaying) {
      this.handlePlayerPause(true);
    } else {
      this.handlePlayerPlay(true);
    }
  };

  handlePlayerPlay = (isManual = false, isJobClipPlay = false) => {
    const { onPlay } = this.props;

    if (onPlay) {
      onPlay();
    }

    if (isManual) {
      if (isJobClipPlay) {
        analytics.track(
          `${this.props.analyticsSrc} Player Pressed Playlist Play`,
          {
            ...this.jobClipAnalyticsPayload(false),
          }
        );
      }

      analytics.track(`${this.props.analyticsSrc} Player Pressed Play`, {
        ...this.jobClipAnalyticsPayload(),
        isJobClipPlay,
      });
    }

    this.resetJobClipTrackTimer();

    this.setState({
      isPlaying: true,
    });
  };

  handlePlayerPause = (isManual = false) => {
    this.stopJobClipTrackTimer();

    if (isManual) {
      analytics.track(`${this.props.analyticsSrc} Player Pressed Pause`, {
        ...this.jobClipAnalyticsPayload(),
      });
    }

    this.setState({
      isPlaying: false,
    });
  };

  handlePlayFromStartTime = (startTime) => {
    const { jobClip } = this.props;

    // Have to figure out which track it is

    const jobClipTracks = jobClip.jobClipTracks;

    jobClipTracks.forEach((jobClipTrack) => {
      const jobClipTrackStartSecs =
        jobClipTrack.recording.audios[0].secRangeStart || 0;
      const jobClipTrackEndSecs =
        jobClipTrack.recording.audios[0].secRangeEnd ||
        jobClipTrack.recording.audios[0].duration;

      // console.log(startTime);
      // console.log(jobClipTrackStartSecs);
      // console.log(jobClipTrackEndSecs);

      if (
        startTime >= jobClipTrackStartSecs &&
        startTime <= jobClipTrackEndSecs
      ) {
        this.setState(
          {
            playingJobClipTrack: jobClipTrack,
          },
          () => {
            this.player.seekTo(startTime, "seconds");
            this.handlePlayerPlay(true);
          }
        );
      }
    });
  };

  getReferrerDomain = () => {
    let referrerUrl = null;
    try {
      referrerUrl = new URL(document.referrer);
    } catch {}
    return referrerUrl && referrerUrl.hostname;
  };

  jobClipAnalyticsPayload = (shouldIncludeJobClipTrack = true) => {
    const { jobClip } = this.props;
    const { playingJobClipTrack } = this.state;
    const queryParams = queryString.parse(document.location.search);

    let payload = {
      ...(this.props.analyticsPayload || {}),
      type: "audio",
      jobClip,
      queryParams,
    };

    if (shouldIncludeJobClipTrack) {
      const playingJobClipTracks = jobClip.jobClipTracks;
      const playingJobClipTrackIndex = playingJobClipTracks.findIndex(
        (jobClipTrack) =>
          jobClipTrack.permalink === playingJobClipTrack.permalink
      );

      payload.jobClipTrackPermalink = playingJobClipTrack.permalink;
      payload.jobClipTrackIndex = playingJobClipTrackIndex;
    }

    return payload;
  };

  resetJobClipTrackTimer = (callingFunctionName = "") => {
    const { isPlaying } = this.state;

    this.stopJobClipTrackTimer();
    if (isPlaying) {
      this.startJobClipTrackTimer();
    }
  };

  startJobClipTrackTimer = () => {
    const { playingJobClipTrack } = this.state;

    this.timePlayingClip = new TimeSpent((data) => {
      const jobClipTrackDuration =
        this.durationFromJobClipTrack(playingJobClipTrack);

      analytics.track(`${this.props.analyticsSrc} Player Time Spent`, {
        ...this.jobClipAnalyticsPayload(),
        duration: data.intervalSec,
        totalDuration: data.totalElapsedSec,
        percentage: jobClipTrackDuration
          ? data.intervalSec / jobClipTrackDuration
          : 0,
        totalPercentage: jobClipTrackDuration
          ? data.totalElapsedSec / jobClipTrackDuration
          : 0,
      });
    });
    this.timePlayingClip.start();
  };

  stopJobClipTrackTimer = () => {
    if (this.timePlayingClip) {
      this.timePlayingClip.stop();
      this.timePlayingClip = null;
    }
  };

  goNextTrack = () => {
    const { playingJobClipTrack } = this.state;
    const { jobClip } = this.props;

    this.stopJobClipTrackTimer();

    const playingJobClipTracks = jobClip.jobClipTracks;
    const playingJobClipTrackIndex = playingJobClipTracks.findIndex(
      (jobClipTrack) => jobClipTrack.permalink === playingJobClipTrack.permalink
    );

    if (playingJobClipTrackIndex < playingJobClipTracks.length - 1) {
      this.setState(
        {
          playingJobClipTrack:
            playingJobClipTracks[playingJobClipTrackIndex + 1],
        },
        () => this.resetJobClipTrackTimer("goNextTrack")
      );
    } else {
      this.setState(
        {
          playingJobClipTrack: playingJobClipTracks[0],
          isPlaying: false,
        },
        () => this.setState({ shouldResetPlayingJobClipTrack: true })
      );
    }
  };

  handlePlayingJobClipTrackReset = () => {
    this.setState({
      shouldResetPlayingJobClipTrack: false,
    });
  };

  handleGetEmbedLinkClick =
    (jobClipTrackPermalink = null) =>
    () => {
      const { jobClip, onGetEmbedLinkClick } = this.props;
      const { publicUrl } = jobClip;
      const { hostname } = window.location;

      const isSameDomain = publicUrl.includes(hostname);

      if (isSameDomain && onGetEmbedLinkClick) {
        onGetEmbedLinkClick(jobClipTrackPermalink);
      } else {
        // Otherwise direct to the embed prompt page
        let embedUrl = publicUrl;
        if (jobClipTrackPermalink) {
          embedUrl += `/track/${jobClipTrackPermalink}`;
        }
        embedUrl += "/embed";
        window.open(embedUrl, "_blank") || window.location.assign(embedUrl);
      }
    };

  handleGoToFullClipClick = () => {
    // If we're on the same domain as the publicUrl, just push history
    const { jobClip, history, location } = this.props;
    const { publicUrl } = jobClip;
    const { hostname } = window.location;

    const isSameDomain = publicUrl.includes(hostname);

    isSameDomain && history && location
      ? history.push({
          pathname: `/job-clip/${jobClip.permalink}`,
          state: { from: location.pathname },
        })
      : window.open(jobClip.publicUrl, "_blank") ||
        window.location.assign(jobClip.publicUrl);
  };

  render() {
    const {
      isPlaying,
      played,
      // playedSeconds,
      playingJobClipTrack,
      remainingSeconds,
      isExpanded,
      isHeightAnimationEnabled,
    } = this.state;
    const {
      className,
      jobClip,
      isExpandable,
      hidePuckBranding,
      onFocus,
      setupOverrides,
      shouldShowTrackTitle,
    } = this.props;

    const duration = jobClip?.jobClipTracks[0]?.recording?.audios[0]?.duration;
    let durationIsoString = "";
    if (duration) {
      // ISO 8601 duration format
      // https://en.wikipedia.org/wiki/ISO_8601
      durationIsoString = "PT" + Math.round(duration) + "S";
    }

    // Figure out title
    let title = "";
    let subtitle = "";
    let headshotSpeakers = null;
    if (jobClip) {
      // Put the host at the end (we'll be phasing out host)
      const sortedSpeakers = sortBy(jobClip.speakers, "isHost");
      // Get speakers from track
      const trackSpeakers = playingJobClipTrack?.speakers;

      // See how many non-host speakers there are (we'll be phasing out host)
      const numGuests = sortedSpeakers.filter(
        (speaker) => !speaker.isHost
      ).length;

      // Headshot
      headshotSpeakers = sortedSpeakers;
      if (jobClip.jobClipTracks.length === 1) {
        // If the track has speakers, show them
        if (jobClip.jobClipTracks[0].speakers.length > 0) {
          headshotSpeakers = jobClip.jobClipTracks[0].speakers;
        }
      } else if (playingJobClipTrack && trackSpeakers.length > 0) {
        headshotSpeakers = trackSpeakers;
      }

      // Title
      if (jobClip.jobClipTracks.length === 1) {
        if (shouldShowTrackTitle) {
          title = jobClip.jobClipTracks[0].title;
        } else {
          const speaker = jobClip.jobClipTracks[0].speakers[0];
          title = `${speaker.firstName} ${speaker.lastName}`;
        }
      } else if (jobClip.type === "team-member") {
        const speaker = sortedSpeakers[0];
        title = `${speaker.firstName} ${speaker.lastName}`;
      } else {
        if (playingJobClipTrack) {
          title = playingJobClipTrack.title;
        } else {
          title = jobClip.title;
        }
      }

      // Subtitle
      if (jobClip.jobClipTracks.length === 1) {
        // If the track has one speaker, show their info
        if (jobClip.jobClipTracks[0].speakers.length === 1) {
          if (shouldShowTrackTitle) {
            const speaker = jobClip.jobClipTracks[0].speakers[0];
            subtitle = `${speaker.firstName} ${speaker.lastName}`;
            if (speaker.title) {
              subtitle += `, ${speaker.title}`;
            }
          } else {
            const speaker = jobClip.jobClipTracks[0].speakers[0];
            subtitle = speaker.title;
          }
        }
      } else if (jobClip.type === "team-member") {
        const speaker = sortedSpeakers[0];
        subtitle = speaker.title;
      } else {
        if (playingJobClipTrack && trackSpeakers.length === 1) {
          const speaker = trackSpeakers[0];
          subtitle = `${speaker.firstName} ${speaker.lastName}`;
          if (speaker.title) {
            subtitle += `, ${speaker.title}`;
          }
        } else if (numGuests === 1) {
          const speaker = sortedSpeakers[0];
          subtitle = `${speaker.firstName} ${speaker.lastName}`;
          if (speaker.title) {
            subtitle += `, ${speaker.title}`;
          }
        }
      }

      // Handle setup overrides
      if (setupOverrides && !playingJobClipTrack) {
        if (setupOverrides.title) {
          title = setupOverrides.title;
        }
        if (setupOverrides.subtitle) {
          subtitle = setupOverrides.subtitle;
        }
        if (setupOverrides.headshotSpeakers) {
          headshotSpeakers = setupOverrides.headshotSpeakers;
        }
      }
    }

    let isSingleTrack = false;
    let singleTrackTimeRemaining = null;
    if (jobClip?.jobClipTracks?.length === 1) {
      isSingleTrack = true;
      const track = jobClip.jobClipTracks[0];

      const jobClipTrackStartSecs =
        track.recording.audios[0].secRangeStart || 0;
      const jobClipTrackEndSecs =
        track.recording.audios[0].secRangeEnd ||
        track.recording.audios[0].duration;

      if (remainingSeconds) {
        singleTrackTimeRemaining = remainingSeconds;
      } else {
        singleTrackTimeRemaining = jobClipTrackEndSecs - jobClipTrackStartSecs;
      }

      // console.log(singleTrackTimeRemaining);
    }

    const brandingData = jobClip?.employerProfile?.brandingData || {};

    return (
      <div>
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org/",
              "@type": "AudioObject",
              name: jobClip.title,
              transcript: jobClip.transcriptionUrl && jobClip.transcription,
              contentUrl:
                jobClip?.jobClipTracks[0]?.recording?.audios[0]?.playbackUrl,
              duration: durationIsoString,
              encodingFormat: "audio/mpeg",
            })}
          </script>
        </Helmet>
        {this.reactPlayer(
          playingJobClipTrack?.recording?.audios[0]?.playbackUrl ||
            this.dummyAudioSrc
        )}
        <SizingContainer>
          <Container
            className={className}
            $backgroundColor={brandingData.secondaryColor}
            color={brandingData.primaryTextColor}
          >
            <PlayerContainer>
              <Headshots speakers={headshotSpeakers} />
              <Info>
                <InfoTitle>{title}</InfoTitle>
                <InfoSubtitle>{subtitle}</InfoSubtitle>
              </Info>
              <RightContainer $hidePuckBranding={hidePuckBranding}>
                {!hidePuckBranding && (
                  <Badge
                    src={imageBadge}
                    alt="Made with Puck"
                    color={brandingData.primaryTextColor}
                  />
                )}
                <PlayerControlsContainer>
                  {isSingleTrack && (
                    <SingleTrackTimer>
                      {dateToText(singleTrackTimeRemaining)}
                    </SingleTrackTimer>
                  )}
                  <PlayPauseButton
                    onFocus={onFocus}
                    $backgroundColor={brandingData.buttonColor}
                    onClick={
                      playingJobClipTrack
                        ? this.handlePlayPauseClick
                        : this.handleJobClipTrackClick(
                            jobClip.jobClipTracks[0],
                            true
                          )
                    }
                    aria-label={isPlaying ? "Pause" : "Play"}
                  >
                    <Icon
                      iconColor={brandingData.buttonTextColor}
                      src={isPlaying ? iconPauseWhite : iconPlayWhite}
                    />
                  </PlayPauseButton>
                  <PopoverMenu
                    brandingData={brandingData}
                    onGetEmbedLinkClick={this.handleGetEmbedLinkClick()}
                    onGoToFullClipClick={this.handleGoToFullClipClick}
                  />
                </PlayerControlsContainer>
              </RightContainer>
            </PlayerContainer>
            {this.progressBar(played)}
            {jobClip.jobClipTracks.length > 1 && (
              <Playlist
                brandingData={brandingData}
                jobClip={jobClip}
                isExpandable={isExpandable}
                isExpanded={isExpanded}
                isHeightAnimationEnabled={isHeightAnimationEnabled}
                onShowMoreClick={this.handleShowMoreClick}
                onShowLessClick={this.handleShowLessClick}
                playingJobClipTrack={playingJobClipTrack}
                onPlaylistItemClick={this.handleJobClipTrackClick}
                onGetEmbedLinkClick={this.handleGetEmbedLinkClick}
                onGoToFullClipClick={this.handleGoToFullClipClick}
              />
            )}
          </Container>
        </SizingContainer>
      </div>
    );
  }
}

Player.propTypes = {
  analyticsSrc: PropTypes.string.isRequired,
};

export default Player;
